import {
  fetchStrategySegments,
  fetchAttentionItems,
  fetchBoardSignals,
  fetchUserStrategyActions,
} from "../../services/businessDashApi";

// ─── Module-level state (populated by loadStrategyData) ──────────────────────

const state = {
  loaded: false,
  segments: [
    { id: "cashflow",   name: "Cash Flow",        status: "stable"      },
    { id: "revenue",    name: "Revenue Growth",   status: "tightening"  },
    { id: "market",     name: "Market Position",  status: "stable"      },
    { id: "people",     name: "People & Culture", status: "tightening"  },
    { id: "operations", name: "Operations",       status: "stable"      },
    { id: "esg",        name: "ESG / Compliance", status: "misaligned"  },
  ],
  attentionItems: [
    {
      id: "ai1",
      segmentId: "revenue",
      headline: "Q2 revenue 8% below projection",
      explanation: "Shortfall driven by delayed enterprise contracts in APAC region.",
    },
    {
      id: "ai2",
      segmentId: "people",
      headline: "Senior leadership succession gap identified",
      explanation: "CFO transition timeline requires board-level visibility and decision on interim plan.",
    },
    {
      id: "ai3",
      segmentId: "esg",
      headline: "Carbon reporting framework not yet adopted",
      explanation: "Mandatory compliance deadline approaching — framework selection requires board approval.",
    },
  ],
  boardSignals: [
    {
      attentionItemId: "ai1",
      comfortable: 20,
      reduceRisk:  45,
      newWays:     25,
      agenda:      10,
      totalVotes:  6,
    },
    {
      attentionItemId: "ai2",
      comfortable: 10,
      reduceRisk:  30,
      newWays:     20,
      agenda:      40,
      totalVotes:  5,
    },
    {
      attentionItemId: "ai3",
      comfortable: 0,
      reduceRisk:  25,
      newWays:     10,
      agenda:      65,
      totalVotes:  4,
    },
  ],
  userActions: [],
};

// ─── Accessor ─────────────────────────────────────────────────────────────────

export const getStrategyData = () => state;

// ─── Loader (called once by MeetingIntegration on mount) ─────────────────────

export const loadStrategyData = async () => {
  if (state.loaded) return;
  try {
    const [segments, items, signals, actions] = await Promise.allSettled([
      fetchStrategySegments(),
      fetchAttentionItems(),
      fetchBoardSignals(),
      fetchUserStrategyActions(),
    ]);
    if (segments.status === "fulfilled" && Array.isArray(segments.value) && segments.value.length) {
      state.segments = segments.value.map((s) => ({
        id:     String(s.id),
        name:   s.name,
        status: s.status || "stable",
      }));
    }
    if (items.status === "fulfilled" && Array.isArray(items.value) && items.value.length) {
      state.attentionItems = items.value.map((i) => ({
        id:          String(i.id),
        segmentId:   String(i.segment_id || i.segmentId),
        headline:    i.headline,
        explanation: i.explanation || "",
      }));
    }
    if (signals.status === "fulfilled" && Array.isArray(signals.value) && signals.value.length) {
      state.boardSignals = signals.value.map((s) => ({
        attentionItemId: String(s.attention_item_id || s.attentionItemId),
        comfortable:     s.comfortable ?? 0,
        reduceRisk:      s.reduce_risk ?? s.reduceRisk ?? 0,
        newWays:         s.new_ways ?? s.newWays ?? 0,
        agenda:          s.agenda ?? 0,
        totalVotes:      s.total_votes ?? s.totalVotes ?? 0,
      }));
    }
    if (actions.status === "fulfilled" && Array.isArray(actions.value)) {
      state.userActions = actions.value.map((a) => ({
        attentionItemId: String(a.attention_item_id || a.attentionItemId),
      }));
    }
    state.loaded = true;
  } catch {
    // Keep mock data
  }
};

// ─── Helpers used by MeetingIntegration's exported functions ─────────────────

export const getActiveAttentionItems = () => {
  const { segments, attentionItems } = state;
  return attentionItems.filter((item) => {
    const seg = segments.find((s) => s.id === item.segmentId);
    return seg && (seg.status === "tightening" || seg.status === "misaligned");
  });
};

export const getRespondedItemIds = () =>
  new Set(state.userActions.map((a) => a.attentionItemId));
